import { useReveal } from "../../lib/hooks";

type Props = {
  src: string;
  alt: string;
  plateNumber: string; // "II", "III", etc.
  caption: string;
  // Optional pull-quote that frames the image
  quote?: { deva?: string; iast?: string; english: string; source?: string };
};

/**
 * An editorial visual rest between sections. A framed image with a small
 * "Plate N" caption, optionally paired with a Sanskrit pull-quote in the
 * margin. Modeled on plate insertions in printed scholarly editions.
 */
export function PlateBreak({ src, alt, plateNumber, caption, quote }: Props) {
  const { ref } = useReveal<HTMLDivElement>();

  return (
    <section
      aria-label={`Plate ${plateNumber} — ${caption}`}
      className="py-12 md:py-20 border-y border-[var(--color-rule)] bg-[var(--color-vellum-dim)]"
    >
      <div className="container-edit">
        <div ref={ref} className="grid grid-cols-12 gap-y-8 lg:gap-x-16 items-center reveal">
          {/* Left — pull quote (optional) */}
          {quote && (
            <div className="col-span-12 lg:col-span-5 order-2 lg:order-1">
              <p className="font-grotesk text-[0.7rem] tracking-[0.22em] uppercase text-[var(--color-violet)] mb-6">
                A teaching
              </p>
              {quote.deva && (
                <p className="font-deva text-[1.6rem] md:text-[1.85rem] leading-[1.5] mb-2 text-[var(--color-ink)]">
                  {quote.deva}
                </p>
              )}
              {quote.iast && (
                <p className="font-display-italic text-[1.15rem] md:text-[1.3rem] mb-6 text-[var(--color-ink-mute)]">
                  {quote.iast}
                </p>
              )}
              <hr className="rule-violet w-10 mb-6" />
              <p className="font-display text-[1.3rem] md:text-[1.5rem] leading-[1.35] text-[var(--color-ink)] max-w-[28ch]">
                “{quote.english}”
              </p>
              {quote.source && (
                <p className="mt-5 font-grotesk text-[0.7rem] tracking-[0.22em] uppercase text-[var(--color-ink-mute)]">
                  — {quote.source}
                </p>
              )}
            </div>
          )}

          {/* Right — image plate */}
          <figure
            className={`col-span-12 ${
              quote ? "lg:col-span-7 order-1 lg:order-2" : "lg:col-span-10 lg:col-start-2"
            }`}
          >
            <div
              className="image-frame aspect-[16/9] sm:aspect-[5/3] md:aspect-[16/9]"
              style={{ outline: "1px solid var(--color-rule-violet)", outlineOffset: "10px" }}
            >
              <img src={src} alt={alt} loading="lazy" />
            </div>
            <figcaption className="mt-9 flex items-baseline gap-3 font-grotesk text-[0.72rem] tracking-[0.18em] uppercase text-[var(--color-ink-mute)]">
              <span>Plate</span>
              <span className="tabular" style={{ color: "var(--color-violet)" }}>
                {plateNumber}.
              </span>
              <span>{caption}</span>
            </figcaption>
          </figure>
        </div>
      </div>
    </section>
  );
}
